// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/tasks.ts (Task, TaskStatus, onboardingTasks order and icons, tasksHeadline), derived from our real data
/**
 * The onboarding's background tasks, as the Tasks panel lists them: what each one is waiting on and where it
 * stands. Pure, like `buildTranscript`, and fed by the same inputs (the browser session, the BFF snapshot and the
 * query data), so the panel and the chat always agree. It never calls `buildTranscript`: where the ad-accounts step
 * stands is the shared `adsStepPosition`, and whether the brand profile is the open step is mirrored here and pinned
 * against `buildTranscript`'s own phase by a test.
 *
 * Plan phase 3, K7 and K0: TP's statuses and look, our real data. A status is what the BFF jobs and the reads
 * report; nothing is timed or scripted, and a number is shown only when the BFF gave one.
 *
 * Order: Brand research, Account profile, Meta ads, Brand guide, Competitive intelligence, then "Funnel capture"
 * only when the BFF started a funnel job. There is no Growth Score row (no data yet).
 */
import { FileText, Filter, Globe, Megaphone, Radar, UserRound, type LucideIcon } from 'lucide-react';

import type {
  AdAnalysisSummary,
  AdPlatform,
  BrandGuideResponse,
  SessionSnapshot,
  StepState,
} from '@/contracts/onboarding-session';

import { analyzingLabel, COPY, countLabel, TASKS } from '../copy';
import { COMPETITOR_PICKS_MAX } from '../constants';
import { answeredQuestions, currentQuestion, namedSupportedPlatforms, researchPending } from './buildTranscript';
import type { DataState, TranscriptData } from './buildTranscript';
import { adsStepPosition } from './adsStepPosition';
import { isTerminalState } from './jobState';
import { researchClock } from './researchState';
import { researchStepsProgress } from './researchSteps';
import type { OnboardingSessionState } from './session';

export type TaskStatus = 'running' | 'needs-you' | 'up-next' | 'done' | 'skipped' | 'partial' | 'unavailable';

export type TaskId = 'research' | 'profile' | 'meta' | 'guide' | 'cohort' | 'funnel';

export interface Task {
  id: TaskId;
  title: string;
  icon: LucideIcon;
  status: TaskStatus;
  detail: string;
  /** Running research: the session clock, for the isolated `m:ss` leaf after the detail (the page never ticks). */
  clock?: { startedAt: number; frozenAt: number | null };
}

/** The brand-guide query, reduced the way the page hands it to the cards. */
export interface TaskGuideState {
  data: BrandGuideResponse | null;
  loading: boolean;
  failed: boolean;
}

/** What `buildTranscript` reads, plus the two reads only the panel needs (the guide and the ad analysis). */
export interface TasksData extends Pick<TranscriptData, 'adAccounts' | 'competitors'> {
  brandGuide?: TaskGuideState;
  /** The cached ad analysis of Meta: only its `coverage.analyzed` is read, and only when it is there. */
  adAnalysis?: DataState<AdAnalysisSummary>;
}

/** A task that has reached an end: finished its work (done, partial, unavailable) or was left out (skipped). */
export const isFinishedStatus = (status: TaskStatus): boolean =>
  status === 'done' || status === 'skipped' || status === 'partial' || status === 'unavailable';

/** True when there are tasks and every one of them is finished: the Onboarding room's dot turns green. */
export function tasksFinished(tasks: readonly Task[]): boolean {
  return tasks.length > 0 && tasks.every((t) => isFinishedStatus(t.status));
}

/** The panel's header: what the tasks are doing as a whole (TP's rules; partial and unavailable count as finished). */
export function tasksHeadline(tasks: readonly Task[]): string {
  const running = tasks.filter((t) => t.status === 'running').length;
  if (running) return TASKS.headline.running(running);
  if (tasks.some((t) => t.status === 'needs-you')) return TASKS.headline.needsYou;
  if (tasks.every((t) => isFinishedStatus(t.status))) return TASKS.headline.allDone;
  return TASKS.headline.upNext;
}

/** The state of a job the snapshot lists; `undefined` when it does not (the BFF has not started it). */
function jobState(snapshot: SessionSnapshot | null, kind: string): StepState | undefined {
  return snapshot?.jobs.find((j) => j.kind === kind)?.state;
}

/* ---------------------------- the flow's position ---------------------------- */

/** Where the ad-accounts step (J2) stands: the first platform that is not past it. The positions come from `adsStepPosition`, as the transcript's do. */
function adsStep(
  session: OnboardingSessionState,
  snapshot: SessionSnapshot | null,
  data: TasksData,
  platforms: AdPlatform[],
): { stage: 'waiting' | 'docked' | 'passed'; platform: AdPlatform | null } {
  for (const platform of platforms) {
    const { kind } = adsStepPosition(session, snapshot, data.adAccounts, platform);
    if (kind === 'waiting') return { stage: 'waiting', platform };
    if (kind === 'docked') return { stage: 'docked', platform };
  }
  return { stage: 'passed', platform: null };
}

/* ---------------------------------- tasks ------------------------------------ */

type Draft = Pick<Task, 'status' | 'detail'> & Partial<Pick<Task, 'clock'>>;

function researchTask(session: OnboardingSessionState, snapshot: SessionSnapshot | null): Draft {
  if (session.url === null) return { status: 'up-next', detail: TASKS.research.startsWithWebsite };
  // A job the snapshot does not list has not started: it neither counts as read nor as failed.
  const states = [jobState(snapshot, 'siteProfile'), jobState(snapshot, 'research')].flatMap((s) => (s === undefined ? [] : [s]));
  // The website is accepted; the BFF has not reported the jobs yet, or has only queued them.
  if (!snapshot || states.length === 0 || states.every((s) => s === 'queued')) {
    return { status: 'up-next', detail: TASKS.research.queued };
  }
  if (states.some((s) => s === 'queued' || s === 'running')) {
    const { startedAt, frozenAt } = researchClock(snapshot);
    return {
      status: 'running',
      detail: TASKS.research.working,
      ...(startedAt !== null ? { clock: { startedAt, frozenAt } } : {}),
    };
  }
  if (states.every((s) => s === 'unavailable')) return { status: 'unavailable', detail: TASKS.research.couldntRead };
  // The same count the research card's header reports (top-level rows), never a second tally.
  const stepsProgress = researchStepsProgress(snapshot);
  const products = snapshot.research.productCount;
  const parts = [
    ...(stepsProgress !== null ? [stepsProgress] : []),
    ...(products != null ? [countLabel(products, 'product', 'products')] : []),
  ];
  const partial = states.some((s) => s === 'partial' || s === 'unavailable');
  return { status: partial ? 'partial' : 'done', detail: parts.join(' · ') || (partial ? TASKS.research.partlyRead : TASKS.research.finished) };
}

function profileTask(session: OnboardingSessionState, snapshot: SessionSnapshot | null, quizDone: boolean): Draft {
  if (session.url === null) return { status: 'up-next', detail: TASKS.profile.startsAfterWebsite };
  if (!quizDone) return { status: 'needs-you', detail: TASKS.profile.answer };
  // The profile is done once the BFF holds the answers, not when the last one is tapped (a failed save is retried).
  if (snapshot?.quizSubmitted !== true) return { status: 'running', detail: TASKS.profile.saving };
  return { status: 'done', detail: countLabel(answeredQuestions(session.answers).length, 'answer', 'answers') };
}

function metaTask(
  snapshot: SessionSnapshot | null,
  data: TasksData,
  quizDone: boolean,
  platforms: AdPlatform[],
  ads: ReturnType<typeof adsStep>,
): Draft {
  if (!quizDone) return { status: 'up-next', detail: TASKS.meta.waitingOnAnswers };
  if (!platforms.includes('meta')) return { status: 'skipped', detail: TASKS.meta.notRunning };
  const adData = data.adAccounts;
  const ready = adData?.state === 'ready' ? adData.data : null;
  const entry = ready?.platforms.find((p) => p.platform === 'meta');
  if (ready && (!entry || entry.status === 'unsupported')) return { status: 'skipped', detail: TASKS.meta.notAvailable };
  if (ads.stage === 'docked' && ads.platform === 'meta') {
    return { status: 'needs-you', detail: TASKS.meta.confirmPages };
  }

  const discovery = jobState(snapshot, 'adDiscovery:meta');
  // The ad pages are still being read or found: the step is not past its confirm yet, so it must not read as
  // finished and then flip back to "needs you" when the pages arrive.
  if (ads.stage === 'waiting' && ads.platform === 'meta') {
    return discovery === 'running'
      ? { status: 'running', detail: TASKS.meta.searching }
      : { status: 'up-next', detail: TASKS.meta.findingPages };
  }
  const analysisData = data.adAnalysis?.state === 'ready' ? data.adAnalysis.data : null;
  // The analysis read is fresher than the snapshot's job (the snapshot trails the poll that moves it).
  const analysis: StepState | undefined = analysisData?.state ?? jobState(snapshot, 'adAnalysis:meta');
  const liveAds = snapshot?.research.liveAdCount ?? null;
  const analyzed = analysisData?.coverage.analyzed ?? null;

  if (!snapshot || discovery === undefined || (discovery === 'queued' && (analysis === undefined || analysis === 'queued'))) {
    return { status: 'up-next', detail: TASKS.meta.queued };
  }
  const analysisPending =
    analysis === 'queued' || analysis === 'running' || (analysis === undefined && liveAds !== null && liveAds > 0 && data.adAnalysis?.state === 'loading');
  if (discovery === 'running' || discovery === 'queued' || (isTerminalState(discovery) && analysisPending)) {
    if (discovery === 'running' || discovery === 'queued') return { status: 'running', detail: TASKS.meta.searching };
    const analyzable = analysisData?.coverage.analyzable ?? 0;
    return {
      status: 'running',
      detail: analysisData && analyzable > 0 ? analyzingLabel(analyzed ?? 0, analyzable) : TASKS.meta.analyzing,
    };
  }
  if (discovery === 'skipped') return { status: 'skipped', detail: TASKS.meta.notRunning };

  const parts = [
    ...(liveAds !== null ? [countLabel(liveAds, 'live ad', 'live ads')] : []),
    ...(analyzed !== null ? [`${analyzed} analyzed`] : []),
  ];
  if (discovery === 'unavailable') return { status: 'unavailable', detail: TASKS.meta.couldntReadAds };
  const partial = discovery === 'partial' || analysis === 'partial' || analysis === 'unavailable';
  return { status: partial ? 'partial' : 'done', detail: parts.join(' · ') || (partial ? TASKS.meta.partlyRead : TASKS.meta.finished) };
}

function guideTask(snapshot: SessionSnapshot | null, guide: TaskGuideState | undefined): Draft {
  if (!snapshot || !guide) return { status: 'up-next', detail: TASKS.guide.startsAfterWebsite };
  if (guide.failed && !guide.data) return { status: 'unavailable', detail: TASKS.guide.couldntBuild };
  // The guide answers from the site at once; the fields only the research fills are still on their way while it works.
  if (guide.loading || !guide.data || researchPending(snapshot)) {
    return { status: 'running', detail: TASKS.guide.reading };
  }
  return { status: 'done', detail: TASKS.guide.ready };
}

function cohortTask(
  session: OnboardingSessionState,
  snapshot: SessionSnapshot | null,
  data: TasksData,
  quizDone: boolean,
  ads: ReturnType<typeof adsStep>,
): Draft {
  const built = session.completedAt !== null;
  if (!quizDone || ads.stage !== 'passed') return { status: 'up-next', detail: TASKS.cohort.startsAfterProfile };
  if (session.brandProfile === null && !built) {
    return { status: 'needs-you', detail: TASKS.cohort.confirmProfile };
  }
  const comp = data.competitors;
  if (!comp || comp.state === 'idle') return { status: 'up-next', detail: TASKS.cohort.startsAfterProfile };
  if (comp.state === 'loading') return { status: 'running', detail: TASKS.cohort.matching };
  if (comp.state === 'error') return { status: 'unavailable', detail: COPY.competitorsFailed[0] };

  const confirmation = snapshot?.confirmations.competitors ?? comp.data.confirmation;
  const list = comp.data.competitors;
  if (confirmation) {
    return confirmation.skipped || confirmation.brandIds.length === 0
      ? { status: 'skipped', detail: TASKS.cohort.skipped }
      : { status: 'done', detail: countLabel(confirmation.brandIds.length, 'competitor', 'competitors') };
  }
  if (built) return { status: 'skipped', detail: TASKS.cohort.skipped };
  if (list.length === 0) {
    return isTerminalState(comp.data.status)
      ? { status: 'partial', detail: COPY.competitorsNone[0] }
      : { status: 'running', detail: TASKS.cohort.matching };
  }
  return { status: 'needs-you', detail: TASKS.cohort.pick(COMPETITOR_PICKS_MAX) };
}

function funnelTask(state: StepState): Draft {
  switch (state) {
    case 'queued':
      return { status: 'up-next', detail: TASKS.funnel.queued };
    case 'running':
      return { status: 'running', detail: TASKS.funnel.working };
    case 'done':
      return { status: 'done', detail: TASKS.funnel.captured };
    case 'partial':
      return { status: 'partial', detail: TASKS.funnel.partial };
    case 'unavailable':
      return { status: 'unavailable', detail: TASKS.funnel.unavailable };
    case 'skipped':
      return { status: 'skipped', detail: TASKS.funnel.skipped };
  }
}

const META: Record<TaskId, { title: string; icon: LucideIcon }> = {
  research: { title: TASKS.title.research, icon: Globe },
  profile: { title: TASKS.title.profile, icon: UserRound },
  meta: { title: TASKS.title.meta, icon: Megaphone },
  guide: { title: TASKS.title.guide, icon: FileText },
  cohort: { title: TASKS.title.cohort, icon: Radar },
  funnel: { title: TASKS.title.funnel, icon: Filter },
};

const task = (id: TaskId, draft: Draft): Task => ({ id, ...META[id], ...draft });

/**
 * The tasks for this moment of the flow. Pure: the same session, snapshot and reads always give the same list.
 */
export function deriveTasks(
  session: OnboardingSessionState,
  snapshot: SessionSnapshot | null,
  data: TasksData = {},
): Task[] {
  const quizDone = session.url !== null && currentQuestion(session.answers) === null;
  const platforms = namedSupportedPlatforms(session.answers);
  const ads = quizDone ? adsStep(session, snapshot, data, platforms) : { stage: 'waiting' as const, platform: null };

  const tasks: Task[] = [
    task('research', researchTask(session, snapshot)),
    task('profile', profileTask(session, snapshot, quizDone)),
    task('meta', metaTask(snapshot, data, quizDone, platforms, ads)),
    task('guide', guideTask(snapshot, data.brandGuide)),
    task('cohort', cohortTask(session, snapshot, data, quizDone, ads)),
  ];
  // The funnel row exists only when the BFF started a funnel job.
  const funnel = jobState(snapshot, 'funnel');
  if (funnel !== undefined) tasks.push(task('funnel', funnelTask(funnel)));
  return tasks;
}
